/**
 * Professional Confirmation Dialog
 *
 * Replaces default browser confirm() with a styled modal.
 */

let dialogElement = null;
let resolvePromise = null;
let hasCheckbox = false;

/**
 * Initialize the confirmation dialog (call once on app start)
 */
export function initConfirmDialog() {
    if (dialogElement) return;

    const html = `
        <div id="confirm-dialog-overlay" class="confirm-overlay"></div>
        <div id="confirm-dialog" class="confirm-dialog">
            <div class="confirm-header">
                <h3 id="confirm-title">Confirm</h3>
            </div>
            <div class="confirm-body">
                <p id="confirm-message"></p>
                <label id="confirm-checkbox-label" class="confirm-checkbox-label hidden">
                    <input type="checkbox" id="confirm-checkbox">
                    <span id="confirm-checkbox-text"></span>
                </label>
            </div>
            <div class="confirm-footer">
                <button id="confirm-cancel" class="confirm-btn cancel">Cancel</button>
                <button id="confirm-ok" class="confirm-btn danger">Delete</button>
            </div>
        </div>
    `;
    document.body.insertAdjacentHTML('beforeend', html);

    dialogElement = document.getElementById('confirm-dialog');
    const overlay = document.getElementById('confirm-dialog-overlay');
    const cancelBtn = document.getElementById('confirm-cancel');
    const okBtn = document.getElementById('confirm-ok');

    // Event listeners
    overlay.addEventListener('click', () => closeDialog(false));
    cancelBtn.addEventListener('click', () => closeDialog(false));
    okBtn.addEventListener('click', () => closeDialog(true));

    // Close on escape
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && dialogElement?.classList.contains('open')) {
            closeDialog(false);
        }
    });
}

/**
 * Show confirmation dialog
 * @param {Object} options - Dialog options
 * @param {string} options.title - Dialog title (default: "Confirm")
 * @param {string} options.message - Message to display
 * @param {string} options.confirmText - Confirm button text (default: "Delete")
 * @param {string} options.cancelText - Cancel button text (default: "Cancel")
 * @param {boolean} options.danger - Use danger styling (default: true)
 * @param {string} [options.checkbox] - Optional checkbox label text. When provided,
 *   result is {confirmed: boolean, checked: boolean} instead of boolean.
 * @returns {Promise<boolean|{confirmed: boolean, checked: boolean}>}
 */
export function confirm({
    title = 'Confirm',
    message,
    confirmText = 'Delete',
    cancelText = 'Cancel',
    danger = true,
    checkbox = null
}) {
    if (!dialogElement) {
        initConfirmDialog();
    }

    hasCheckbox = !!checkbox;

    // Update content
    document.getElementById('confirm-title').textContent = title;
    document.getElementById('confirm-message').textContent = message;

    // Checkbox
    const checkboxLabel = document.getElementById('confirm-checkbox-label');
    const checkboxInput = document.getElementById('confirm-checkbox');
    const checkboxText = document.getElementById('confirm-checkbox-text');
    if (checkbox) {
        checkboxLabel.classList.remove('hidden');
        checkboxText.textContent = checkbox;
        checkboxInput.checked = false;
    } else {
        checkboxLabel.classList.add('hidden');
    }

    const okBtn = document.getElementById('confirm-ok');
    okBtn.textContent = confirmText;
    okBtn.className = `confirm-btn ${danger ? 'danger' : 'primary'}`;

    document.getElementById('confirm-cancel').textContent = cancelText;

    // Show dialog
    document.getElementById('confirm-dialog-overlay').classList.add('active');
    dialogElement.classList.add('open');
    document.body.style.overflow = 'hidden';

    // Focus the cancel button for safety
    document.getElementById('confirm-cancel').focus();

    return new Promise(resolve => {
        resolvePromise = resolve;
    });
}

/**
 * Close the dialog
 */
function closeDialog(confirmed) {
    document.getElementById('confirm-dialog-overlay')?.classList.remove('active');
    dialogElement?.classList.remove('open');
    document.body.style.overflow = '';

    if (resolvePromise) {
        if (hasCheckbox) {
            const checked = document.getElementById('confirm-checkbox')?.checked || false;
            resolvePromise({ confirmed, checked });
        } else {
            resolvePromise(confirmed);
        }
        resolvePromise = null;
    }
}
